/**
 * Search has to work the way a Panamanian owner types.
 *
 * They will type "maria" looking for "María" and "nunez" looking for "Núñez",
 * on a phone keyboard where reaching the accent is three taps. So matching
 * folds case and strips diacritics on both sides.
 *
 * The fold is a hand-written table rather than String.normalize("NFD") plus a
 * combining-marks regex because SQL mirrors it letter for letter: `translate`
 * in `backend/src/db/list.ts` and `0032_party.sql`, and every stored `*_key`
 * column. Widening it here alone would make the server and the client
 * disagree about what a search finds.
 *
 * Which fold where: `foldForSearch` for search and the stored `*_key`
 * columns; `foldAccents` for slugs, lookup keys, header words and the other
 * file names; `documentFileName` keeps `foldForSearch` (see there).
 *
 * Shared rather than server-side only. The obvious backend answer is Postgres'
 * `unaccent`, and it was not taken: it is an extension the deploy would have to
 * install, its folding is not this table's, and the day the phone filters a
 * cached list locally the two would disagree about whether "nunez" finds
 * "Núñez". One implementation, on both sides.
 */
const FOLD = {
    á: "a", à: "a", ä: "a", â: "a", ã: "a",
    é: "e", è: "e", ë: "e", ê: "e",
    í: "i", ì: "i", ï: "i", î: "i",
    ó: "o", ò: "o", ö: "o", ô: "o", õ: "o",
    ú: "u", ù: "u", ü: "u", û: "u",
    ñ: "n", ç: "c",
};
export function foldForSearch(value) {
    let out = "";
    for (const char of value.toLowerCase())
        out += FOLD[char] ?? char;
    return out;
}
/** Every combining mark NFD splits off, removed; case kept. For file names, slugs and header words, not for search. */
export function foldAccents(text) {
    return text.normalize("NFD").replace(/\p{M}/gu, "");
}
/** True when every word of the query appears somewhere in the haystacks. */
export function matchesQuery(query, ...haystacks) {
    const words = foldForSearch(query).split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return true;
    const hay = haystacks.map((h) => foldForSearch(h)).join(" ");
    return words.every((word) => hay.includes(word));
}
